import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Channel as StreamChannelType } from "stream-chat";
import { ChannelList, useChatContext } from "stream-chat-react";
import {
  Archive,
  AtSign,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Search,
  UserPlus,
  Users,
  X,
} from "lucide-react";

import {
  ArchivedChannelPreview,
  ChannelPreviewItem,
  MentionChannelPreview,
} from "@/features/chat/components/ChannelPreviewItem";
import { useArchivedChannels } from "@/features/chat/hooks/useArchivedChannels";
import { useMentionTracking } from "@/features/chat/hooks/useMentionTracking";

type ChannelFilter = "all" | "unread" | "mentioned" | "groups";

const CHANNEL_LIST_EXPANDED_KEY = "hp-chat-channel-list-expanded";

// Paginator for ChannelList — shows a "Load more" button when more pages exist
const ChannelListPaginator = ({
  children,
  hasNextPage,
  isLoading,
  loadNextPage,
}: {
  children?: React.ReactNode;
  hasNextPage?: boolean;
  isLoading?: boolean;
  loadNextPage?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {children}
      {hasNextPage && (
        <button
          onClick={loadNextPage}
          disabled={isLoading}
          className="w-full py-2.5 text-sm text-indigo-600 hover:bg-indigo-50 border-t border-slate-100 disabled:opacity-50 transition-colors"
        >
          {isLoading ? t("chat.loading") : t("chat.load_more_conversations")}
        </button>
      )}
    </>
  );
};

// Quiet uppercase section label (Pinned / Mentions / Messages)
const SectionHeader = ({
  icon,
  label,
  count,
}: {
  icon?: React.ReactNode;
  label: string;
  count?: number;
}) => (
  <div className="px-4 pt-3 pb-1.5 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
        {label}
      </span>
      {count !== undefined && (
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">
          {count}
        </span>
      )}
    </div>
  </div>
);

export const ChannelListPanel = ({
  userId,
  channelListKey,
  onRefreshList,
  mobileHidden,
  onNewDirect,
  onNewGroup,
  onPinChannel,
  onUnpinChannel,
  onArchiveChannel,
  onSelectArchived,
  onSelectMention,
  onListClick,
}: {
  userId: string;
  channelListKey: number;
  onRefreshList: () => void;
  mobileHidden: boolean;
  onNewDirect: () => void;
  onNewGroup: () => void;
  onPinChannel: (ch: StreamChannelType) => void;
  onUnpinChannel: (ch: StreamChannelType) => void;
  onArchiveChannel: (ch: StreamChannelType) => void;
  onSelectArchived: (ch: StreamChannelType) => void;
  onSelectMention: (ch: StreamChannelType, messageId?: string) => void;
  onListClick: () => void;
}) => {
  const { t } = useTranslation();
  const { channel, client } = useChatContext();

  const [localSearch, setLocalSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ChannelFilter>("all");
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      return window.localStorage.getItem(CHANNEL_LIST_EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const {
    mentionedCids,
    latestMentionIds,
    isLoading: mentionsLoading,
  } = useMentionTracking(userId);
  const archived = useArchivedChannels(userId, onRefreshList);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(CHANNEL_LIST_EXPANDED_KEY, String(next));
      } catch {
        // The panel can still resize when storage is unavailable.
      }
      return next;
    });
  }, []);

  const filters = useMemo(
    () => ({
      type: "messaging" as const,
      members: { $in: [userId] },
      archived: false,
      ...(activeFilter === "groups" ? { member_count: { $gt: 2 } } : {}),
      ...(activeFilter === "mentioned" &&
      !mentionsLoading &&
      mentionedCids.size > 0
        ? { cid: { $in: [...mentionedCids] } }
        : {}),
    }),
    [userId, activeFilter, mentionedCids, mentionsLoading],
  );

  const sort = useMemo(() => ({ last_message_at: -1 as const }), []);

  // Re-fetch channel list when filter changes
  useEffect(() => {
    onRefreshList();
  }, [activeFilter, onRefreshList]);

  // Split channels into Pinned / Mentions / Normal sections
  const renderChannels = useCallback(
    (channels: StreamChannelType[]) => {
      // Client-side substring search across channel name and member names
      let filteredChannels = localSearch.trim()
        ? channels.filter((ch) => {
            const q = localSearch.trim().toLowerCase();
            const name = (ch.data as { name?: string } | undefined)?.name?.toLowerCase();
            if (name && name.includes(q)) return true;
            // For DMs, check the other member's name
            return Object.values(ch.state.members).some((m) => {
              if (m.user?.id === userId) return false;
              return (m.user?.name as string | undefined)?.toLowerCase().includes(q);
            });
          })
        : channels;

      // Client-side filter tab filtering
      if (activeFilter === "unread") {
        filteredChannels = filteredChannels.filter(
          (ch) => (ch.state.unreadCount ?? 0) > 0,
        );
      } else if (activeFilter === "mentioned") {
        filteredChannels = filteredChannels.filter((ch) =>
          mentionedCids.has(ch.cid),
        );
      }

      const pinnedChannels: StreamChannelType[] = [];
      const mentionChannels: StreamChannelType[] = [];
      const normalChannels: StreamChannelType[] = [];

      for (const ch of filteredChannels) {
        const membership = ch.state.membership;
        if (activeFilter === "mentioned") {
          mentionChannels.push(ch);
        } else if (membership?.pinned_at) {
          pinnedChannels.push(ch);
        } else if ((ch.countUnreadMentions() ?? 0) > 0) {
          mentionChannels.push(ch);
        } else {
          normalChannels.push(ch);
        }
      }

      const hasSections =
        pinnedChannels.length > 0 ||
        mentionChannels.length > 0 ||
        normalChannels.length > 0;

      return (
        <>
          {/* ── Pinned ── */}
          {pinnedChannels.length > 0 && (
            <div>
              <SectionHeader
                icon={<Pin className="w-3 h-3 text-amber-500" />}
                label={t("chat.section_pinned")}
                count={pinnedChannels.length}
              />
              {pinnedChannels.map((ch) => (
                <ChannelPreviewItem
                  key={ch.cid}
                  channel={ch}
                  active={channel?.cid === ch.cid}
                  clientUserId={userId}
                  pinned
                  onUnpin={() => onUnpinChannel(ch)}
                  onArchive={() => onArchiveChannel(ch)}
                />
              ))}
            </div>
          )}

          {/* ── Mentions ── */}
          {mentionChannels.length > 0 && (
            <div>
              <SectionHeader
                icon={<AtSign className="w-3 h-3 text-red-500" />}
                label={t("chat.section_mentions")}
                count={mentionChannels.length}
              />
              {mentionChannels.map((ch) => (
                <MentionChannelPreview
                  key={ch.cid}
                  channel={ch}
                  active={channel?.cid === ch.cid}
                  clientUserId={userId}
                  onSelect={() =>
                    onSelectMention(ch, latestMentionIds.get(ch.cid))
                  }
                />
              ))}
            </div>
          )}

          {/* ── Messages ── */}
          {normalChannels.length > 0 && (
            <div>
              {(pinnedChannels.length > 0 || mentionChannels.length > 0) && (
                <SectionHeader label={t("chat.section_messages")} />
              )}
              {normalChannels.map((ch) => (
                <ChannelPreviewItem
                  key={ch.cid}
                  channel={ch}
                  active={channel?.cid === ch.cid}
                  clientUserId={userId}
                  onPin={() => onPinChannel(ch)}
                  onArchive={() => onArchiveChannel(ch)}
                />
              ))}
            </div>
          )}

          {!hasSections && (
            <div className="flex flex-col items-center justify-center py-8 gap-1">
              <span className="text-slate-400 text-sm">
                {activeFilter === "unread"
                  ? t("chat.no_unread")
                  : activeFilter === "mentioned"
                  ? t("chat.no_mentioned")
                  : activeFilter === "groups"
                  ? t("chat.no_groups")
                  : t("chat.no_conversations")}
              </span>
            </div>
          )}
        </>
      );
    },
    [channel?.cid, userId, localSearch, activeFilter, mentionedCids, latestMentionIds, onPinChannel, onUnpinChannel, onArchiveChannel, onSelectMention, t],
  );

  return (
    <div
      className={`w-full ${isExpanded ? "md:w-[30rem]" : "md:w-80"} md:flex-shrink-0 border-r border-slate-200/80 glass flex flex-col transition-[width] duration-200 ease-out ${
        mobileHidden ? "hidden md:flex" : "flex"
      }`}
    >
      <div className="p-3 pb-2 border-b border-slate-200/80">
        {/* Local search input — substring match on name / group name */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex flex-1 min-w-0 items-center gap-2 bg-slate-100/80 rounded-xl px-3 h-9 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              placeholder={t("chat.search_users")}
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm"
            />
            {localSearch && (
              <button onClick={() => setLocalSearch("")} className="text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={toggleExpanded}
            aria-label={t(isExpanded ? "chat.collapse_conversations" : "chat.expand_conversations")}
            aria-pressed={isExpanded}
            title={t(isExpanded ? "chat.collapse_conversations" : "chat.expand_conversations")}
            className="hidden md:inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 transition-colors"
          >
            {isExpanded ? (
              <PanelLeftClose className="w-4.5 h-4.5" />
            ) : (
              <PanelLeftOpen className="w-4.5 h-4.5" />
            )}
          </button>
        </div>

        {/* Filter tabs — segmented control */}
        <div className="flex gap-0.5 mb-2 bg-slate-100 rounded-lg p-0.5">
          {(
            [
              { key: "all", label: t("chat.filter_all") },
              { key: "unread", label: t("chat.filter_unread") },
              { key: "mentioned", label: t("chat.filter_mentioned") },
              { key: "groups", label: t("chat.filter_groups") },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors truncate ${
                activeFilter === key
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* New chat buttons */}
        <div className="flex gap-2 mb-1">
          <button
            onClick={onNewDirect}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 md:py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            {t("chat.direct")}
          </button>
          <button
            onClick={onNewGroup}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 md:py-2 bg-indigo-50 text-indigo-700 text-sm rounded-lg hover:bg-indigo-100 active:bg-indigo-200 transition-colors"
          >
            <Users className="w-4 h-4" />
            {t("chat.group")}
          </button>
        </div>
      </div>

      {/* Channel list from Stream - clicking a channel auto-sets it active via context */}
      <div
        className="flex-1 overflow-y-auto hp-channel-scroll"
        onClick={onListClick}
      >
        <ChannelList
          key={channelListKey}
          filters={filters}
          sort={sort}
          options={{ limit: 30 }}
          setActiveChannelOnMount={false}
          renderChannels={renderChannels}
          Paginator={ChannelListPaginator}
        />

        {/* Collapsible Archived Conversations Section */}
        <div className="border-t border-slate-200/80 mt-1">
          <button
            onClick={() => archived.setExpanded(!archived.expanded)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Archive className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">
                {t("chat.archived_conversations")}
              </span>
              {archived.channels.length > 0 && (
                <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">
                  {archived.channels.length}
                </span>
              )}
            </div>
            <ChevronDown
              className={`w-4 h-4 text-slate-400 transition-transform ${archived.expanded ? "rotate-180" : ""}`}
            />
          </button>

          {archived.expanded && (
            <div className="bg-slate-50/50">
              {archived.loading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-400" />
                </div>
              ) : archived.channels.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-4">
                  {t("chat.no_archived")}
                </p>
              ) : (
                <>
                  {archived.channels.map((ch) => (
                    <ArchivedChannelPreview
                      key={ch.cid}
                      channel={ch}
                      onSelect={onSelectArchived}
                      onUnarchive={archived.unarchive}
                      clientUserId={client.userID || ""}
                    />
                  ))}
                  {archived.hasMore && (
                    <button
                      onClick={archived.loadMore}
                      disabled={archived.loadingMore}
                      className="w-full py-2.5 text-sm text-indigo-600 hover:bg-indigo-50 border-t border-slate-100 disabled:opacity-50 transition-colors"
                    >
                      {archived.loadingMore ? t("chat.loading") : t("chat.load_more_archived")}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
